import { Link, useNavigate, useParams } from "react-router-dom";
import AdminNavBar from "./components/AdminNavBar";
import { convertToMegabytes, handleBackToSiteMaterial } from "./adminHelper";
import { ChangeEvent, useState } from "react";
import { useDropzone } from "react-dropzone";
import apiService from "../services/api.service";
import adminUseFetchChapterSolutions from "../custom_hook/adminUseFetchChapterSolutions";
import adminUseFetchBookChapters from "../custom_hook/adminUseFetchBookChapters";
import { AxiosResponse } from "axios";

const SolutionForm = () => {
  const { bookId, chapterId } = useParams();
  const { book } = adminUseFetchBookChapters(bookId || "");
  const { chapter } = adminUseFetchChapterSolutions(chapterId || "");
  const navigate = useNavigate();

  const [solution, setSolution] = useState({
    exercise_type: "",
    exercise_number: "",
    answer: "",
    chapter: chapter?.id,
    uploaded_images: [] as File[],
  });

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setSolution({
      ...solution,
      [e.target.name]: e.target.value,
    });
  };

  const onDrop = (acceptedFiles: File[]) => {
    setSolution((prevSolution) => ({
      ...prevSolution,
      uploaded_images: [...prevSolution.uploaded_images, ...acceptedFiles],
    }));
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  const handleRemoveImage = (index: number) => {
    setSolution((prevSolution) => {
      const updatedImages = [...prevSolution.uploaded_images];
      updatedImages.splice(index, 1); // Remove the image at the specified index
      return {
        ...prevSolution,
        uploaded_images: updatedImages,
      };
    });
  };

  const handleSubmitSolution = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("exercise_type", solution.exercise_type);
    formData.append("exercise_number", solution.exercise_number);
    formData.append("answer", solution.answer);
    formData.append("chapter", chapter?.id.toString() || "");
    solution.uploaded_images.forEach((image) => {
      formData.append("uploaded_images", image);
    });

    const makePostRequest = async () => {
      try {
        await apiService.post<AxiosResponse>("/solutions/", formData);
        alert("Solution created successfully");
        setSolution({
          exercise_type: "",
          exercise_number: "",
          answer: "",
          chapter: chapter?.id,
          uploaded_images: [],
        });
      } catch (error: any) {
        console.error("Error occured while creating solution", error);
        if (error.response && error.response.status === 401) {
          throw new Error("Token expired");
        }
        throw error;
      }
    };

    try {
      await makePostRequest();
      // Reset the file input field (if using a controlled component)
      const fileInput = document.getElementById(
        "formFileInput"
      ) as HTMLInputElement;
      if (fileInput) {
        fileInput.value = ""; // Reset the input value to clear the selected file
      }
    } catch (error: any) {
      console.error("Error occurred while creating solution", error);
      alert("Failed to create solution. Please try again.");
    }
  };

  return (
    <>
      <nav className="navbar navbar-dark bg-dark">
        <AdminNavBar />
      </nav>
      <nav id="bc-nav" aria-label="breadcrumb">
        <ol id="bc-ol" className="breadcrumb">
          <li id="bc-books" className="breadcrumb-item">
            <button
              className="inactive-anchor-link"
              onClick={() => {
                handleBackToSiteMaterial(navigate);
              }}
            >
              Site Materials
            </button>{" "}
            <span className="greater-than">&#x02AA2;</span>
          </li>
          <Link
            to={`/admin/site-materials/books`}
            className="breadcrumb-item inactive-anchor-link"
          >
            Books <span className="greater-than">&#x02AA2;</span>
          </Link>

          <Link
            to={`/admin/site-materials/books/${bookId}/chapters`}
            className="breadcrumb-item inactive-anchor-link"
          >
            {book?.title} <span className="greater-than">&#x02AA2;</span>
          </Link>

          <Link
            to={`/admin/site-materials/books/${bookId}/chapters/${chapterId}/solutions`}
            className="breadcrumb-item inactive-anchor-link"
          >
            {chapter?.title} <span className="greater-than">&#x02AA2;</span>
          </Link>
          <li id="bc-active" className="breadcrumb-item active">
            Create Solution
          </li>
        </ol>
      </nav>
      <form id="chapter-form" className="mb-3" onSubmit={handleSubmitSolution}>
        <div className="mb-3">
          <label className="form-label"> Exercise Type </label>
          <select
            className="form-select"
            name="exercise_type"
            value={solution.exercise_type}
            onChange={handleInputChange}
            required
          >
            <option value="Exercise">Exercise</option>
            <option value="Review Question">Review Question</option>
          </select>
        </div>
        <div className="mb-3">
          <label className="form-label"> Question Number</label>
          <input
            className={"form-control"}
            type="text"
            name="exercise_number"
            value={solution.exercise_number}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="mb-3">
          <label>Solution Images</label>
          <div id="dropzone" {...getRootProps()}>
            <input {...getInputProps()} />
            <p>Drag 'n' drop image files here, or click to select files</p>
            <div id="dropzone-add"> &#43; </div>
          </div>
        </div>
        <ul>
          {solution.uploaded_images.map((file, index) => (
            <li key={index}>
              {file.name} {convertToMegabytes(file.size) + "MB"}
              <button
                className="btn"
                id="delete-upload-btn"
                type="button"
                onClick={() => handleRemoveImage(index)}
              >
                &#935;
              </button>
            </li>
          ))}
        </ul>
        <div className="mb-3">
          <label className="form-label"> Answer </label>
          <textarea
            className={"form-control"}
            name="answer"
            value={solution.answer}
            onChange={handleInputChange}
            required
            rows={28}
          />
        </div>
        <button
          id="create-post-btn"
          className="btn error-page-btn"
          type="submit"
        >
          Add Solution
        </button>
      </form>
    </>
  );
};

export default SolutionForm;
